import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  useMondayImport,
  useMondayUsers,
  shouldExcludeGroup,
  type ImportResponse,
  type ImportOverride,
  type PreviewItem,
} from "@/hooks/use-monday-import";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { ImportGroupSelector } from "@/components/admin/ImportGroupSelector";
import { ImportLog } from "@/components/admin/ImportLog";
import { ImportPreviewTable } from "@/components/admin/ImportPreviewTable";
import { ImportProjectSelector } from "@/components/admin/ImportProjectSelector";
import { MondayPhaseStatusSync } from "@/components/admin/MondayPhaseStatusSync";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";

async function fetchBoardGroupsClient(
  boardId: number,
): Promise<Array<{ id: string; title: string }>> {
  const url = new URL(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/monday-board-groups`,
  );
  url.searchParams.set("board_id", String(boardId));
  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  });
  if (!res.ok) throw new Error(`monday-board-groups (${res.status})`);
  const json = (await res.json()) as {
    groups?: Array<{ id: string; title: string }>;
  };
  return json.groups ?? [];
}

/**
 * Manuell import av tidsregistreringer fra Monday-ansattboards. Velg ansatt(e),
 * grupper, kjør dry-run, justér i preview, bekreft.
 */
export function TimeImportTab() {
  const { activeUser } = useImpersonation();
  const { data: candidateUsers = [], isLoading: usersLoading } = useMondayUsers();
  const importMut = useMondayImport();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedProjectCodes, setSelectedProjectCodes] = useState<string[]>([]);
  const [lastResult, setLastResult] = useState<ImportResponse | null>(null);
  const [overrides, setOverrides] = useState<Record<string, ImportOverride>>({});
  const [running, setRunning] = useState(false);

  const isMulti = selectedUserIds.length > 1;
  const singleUserId = selectedUserIds.length === 1 ? selectedUserIds[0] : null;
  const singleUser = singleUserId
    ? candidateUsers.find((u) => u.id === singleUserId)
    : null;
  const singleBoardId = singleUser?.monday_board_id ?? null;

  const toggleUser = (id: string) => {
    setSelectedUserIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      setSelectedGroupIds([]);
      setLastResult(null);
      setOverrides({});
      return next;
    });
  };

  const handleRun = async (dryRun: boolean) => {
    if (selectedUserIds.length === 0) return;
    if (!isMulti && selectedGroupIds.length === 0) return;
    setRunning(true);
    try {
      const aggregated: ImportResponse = {
        success: true,
        stats: {
          totalInGroups: 0,
          imported: 0,
          skipped: 0,
          skippedByAdmin: 0,
          matchDistribution: {},
          assignmentsUpserted: 0,
          assignmentsZeroed: 0,
        },
        preview: [],
      };

      for (const userId of selectedUserIds) {
        const u = candidateUsers.find((x) => x.id === userId);
        if (!u?.monday_board_id) continue;

        let groupIds = selectedGroupIds;
        if (isMulti) {
          const allGroups = await fetchBoardGroupsClient(Number(u.monday_board_id));
          groupIds = allGroups
            .filter((g) => !shouldExcludeGroup(g.title))
            .map((g) => g.id);
          if (groupIds.length === 0) continue;
        }

        const res = await importMut.mutateAsync({
          userId,
          boardId: Number(u.monday_board_id),
          groupIds,
          dryRun,
          triggeredBy: activeUser?.id ?? null,
          overrides: dryRun ? undefined : overrides,
          projectCodes:
            selectedProjectCodes.length > 0 ? selectedProjectCodes : undefined,
        });

        if (res.stats && aggregated.stats) {
          aggregated.stats.totalInGroups += res.stats.totalInGroups;
          aggregated.stats.imported += res.stats.imported;
          aggregated.stats.skipped += res.stats.skipped;
          aggregated.stats.skippedByAdmin =
            (aggregated.stats.skippedByAdmin ?? 0) + (res.stats.skippedByAdmin ?? 0);
          for (const [k, v] of Object.entries(res.stats.matchDistribution ?? {})) {
            aggregated.stats.matchDistribution[k] =
              (aggregated.stats.matchDistribution[k] ?? 0) + v;
          }
          aggregated.stats.assignmentsUpserted =
            (aggregated.stats.assignmentsUpserted ?? 0) +
            (res.stats.assignmentsUpserted ?? 0);
          aggregated.stats.assignmentsZeroed =
            (aggregated.stats.assignmentsZeroed ?? 0) +
            (res.stats.assignmentsZeroed ?? 0);
        }
        if (res.preview) {
          const tagged: PreviewItem[] = res.preview.map((p) => ({
            ...p,
            name: isMulti ? `${u.full_name}: ${p.name}` : p.name,
          }));
          aggregated.preview!.push(...tagged);
        }
      }

      setLastResult(aggregated);
      if (!dryRun) setOverrides({});
    } catch {
      /* toast håndteres i hook */
    } finally {
      setRunning(false);
    }
  };

  const handleOverrideChange = (key: string, patch: ImportOverride) => {
    setOverrides((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Konfigurer import</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Ansatte ({selectedUserIds.length})
              </label>
              {candidateUsers.length > 0 && (
                <button
                  type="button"
                  className="text-[11px] text-muted-foreground hover:text-primary"
                  onClick={() => {
                    if (selectedUserIds.length === candidateUsers.length) {
                      setSelectedUserIds([]);
                    } else {
                      setSelectedUserIds(candidateUsers.map((u) => u.id));
                    }
                    setSelectedGroupIds([]);
                    setLastResult(null);
                    setOverrides({});
                  }}
                >
                  {selectedUserIds.length === candidateUsers.length
                    ? "Fjern alle"
                    : "Velg alle"}
                </button>
              )}
            </div>
            {usersLoading ? (
              <p className="text-sm text-muted-foreground">Henter ansatte...</p>
            ) : candidateUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Ingen ansatte med Monday-board
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 max-w-3xl">
                {candidateUsers.map((u) => {
                  const sel = selectedUserIds.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => toggleUser(u.id)}
                      className={cn(
                        "flex items-center gap-2 px-2.5 py-1.5 rounded border text-left text-sm hover:bg-muted/40 transition-colors",
                        sel
                          ? "border-primary bg-primary/10"
                          : "border-foreground/30",
                      )}
                    >
                      <span
                        className={cn(
                          "w-4 h-4 rounded border-[1.5px] border-foreground/40 flex items-center justify-center shrink-0",
                          sel && "bg-primary border-primary",
                        )}
                      >
                        {sel && (
                          <SrIcon
                            name="check"
                            className="w-2.5 h-2.5 text-primary-foreground"
                          />
                        )}
                      </span>
                      <span className="truncate">{u.full_name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Grupper
            </label>
            {isMulti ? (
              <p className="text-[11px] text-muted-foreground">
                Flere ansatte er valgt — alle ikke-ekskluderte grupper på hvert
                ansatt-board hentes automatisk.
              </p>
            ) : (
              <ImportGroupSelector
                boardId={singleBoardId ? Number(singleBoardId) : null}
                selectedGroupIds={selectedGroupIds}
                onChange={(ids) => {
                  setSelectedGroupIds(ids);
                  setLastResult(null);
                  setOverrides({});
                }}
              />
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Filtrér til prosjekter (valgfritt)
            </label>
            <p className="text-[11px] text-muted-foreground">
              Tom = importér alle prosjekter i valgte grupper. Velg ett eller
              flere for å rydde opp ett prosjekt om gangen.
            </p>
            <ImportProjectSelector
              selectedCodes={selectedProjectCodes}
              onChange={(codes) => {
                setSelectedProjectCodes(codes);
                setLastResult(null);
                setOverrides({});
              }}
            />
          </div>

          <div className="flex items-center gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => handleRun(true)}
              disabled={
                running ||
                selectedUserIds.length === 0 ||
                (!isMulti && selectedGroupIds.length === 0)
              }
            >
              {running ? (
                <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <SrIcon name="eye" className="w-4 h-4 mr-1.5" />
              )}
              Dry-run
            </Button>
            <Button
              onClick={() => handleRun(false)}
              disabled={
                running ||
                selectedUserIds.length === 0 ||
                (!isMulti && selectedGroupIds.length === 0) ||
                !lastResult ||
                lastResult.stats?.totalInGroups === 0
              }
              title={!lastResult ? "Kjør dry-run først" : undefined}
            >
              {running ? (
                <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <SrIcon name="check" className="w-4 h-4 mr-1.5" />
              )}
              Bekreft import
            </Button>
            {lastResult?.stats && (
              <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                {lastResult.stats.totalInGroups} items —{" "}
                {lastResult.stats.imported} klar for import,{" "}
                {lastResult.stats.skipped} hoppes over
                {(lastResult.stats.skippedByAdmin ?? 0) > 0 &&
                  ` (${lastResult.stats.skippedByAdmin} av admin)`}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {lastResult?.preview && lastResult.preview.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Forhåndsvisning og redigering</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Justér fase og budgetlinje per item. Slå av enkelte linjer med
              skip-toggle hvis du ikke vil importere dem.
            </p>
          </CardHeader>
          <CardContent>
            <ImportPreviewTable
              preview={lastResult.preview}
              overrides={overrides}
              onOverrideChange={handleOverrideChange}
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Import-logg</CardTitle>
        </CardHeader>
        <CardContent>
          <ImportLog userId={singleUserId} entityType="time_entries" />
        </CardContent>
      </Card>

      {/* VIB-724b: Sync fase-status fra Monday → Sølvrev task_status */}
      <MondayPhaseStatusSync />
    </div>
  );
}
